/**
 * PredispositionProfileHelpTooltip — Phase 1.7 noun-rollout primer.
 *
 * Per /shared/V3_Propagation_Closure_2026-05-19.md Part D + design mockup at
 * /design/mockups/Phase3_Profile_ListView.html (the powder-blue Remember-
 * register tooltip directly under the navy header strip on the Profile list).
 *
 * Renders once per browser; dismissable via × button; never re-shown after
 * dismissal until the `.v1` storage key is bumped to `.v2` in a future
 * re-lock.
 *
 * Locked behavior:
 *   - localStorage key: `pp.help.profile-noun.dismissed.v1`
 *   - Conditional render (NOT visibility-hidden) so dismissed state is
 *     absent from the tab order
 *   - ARIA: role="status" + aria-live="polite" on the container so screen
 *     readers announce on first paint without interrupting
 *   - Dismiss button: aria-label="Dismiss help tooltip"
 *   - No entrance animation (reduced-motion friendly; the tooltip mounts
 *     static)
 *
 * Locked copy:
 *   > **About Predisposition Profiles**
 *   > Predisposition Profiles are the structured behavioral read for each
 *   > person on your team. You'll see them shortened to "Profile" throughout
 *   > the product. Read more on The Science Behind It →
 */

import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const STORAGE_KEY = "pp.help.profile-noun.dismissed.v1";

export default function PredispositionProfileHelpTooltip() {
  // Start with `null` so the first render emits nothing — we don't know yet
  // whether the user dismissed it on a prior visit. The useEffect below
  // reads localStorage and sets the real state. This avoids a "flash of
  // tooltip" for users who already dismissed it.
  const [shouldRender, setShouldRender] = useState(null);

  useEffect(() => {
    try {
      const dismissed = window.localStorage.getItem(STORAGE_KEY) === "true";
      setShouldRender(!dismissed);
    } catch {
      // localStorage access can throw under strict privacy modes / sandbox
      // contexts. Default to "show" (defensive — the primer is benign).
      setShouldRender(true);
    }
  }, []);

  const dismiss = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, "true");
    } catch {
      /* ignore — see comment above */
    }
    setShouldRender(false);
  };

  // First-render gate + dismissed gate. Conditional render (NOT
  // visibility-hidden) per spec so the tooltip is fully absent from the
  // DOM + tab order after dismissal.
  if (!shouldRender) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="predisposition-profile-help-tooltip"
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 14,
        padding: "16px 18px 18px",
        marginBottom: 20,
        background: "var(--ppr-color-background-surface)",
        border: "1.5px solid var(--ppr-color-accent-powder)",
        borderLeft: "4px solid var(--ppr-color-accent-powder)",
        position: "relative",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          flex: "0 0 auto",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 28,
          height: 28,
          borderRadius: "50%",
          background: "var(--ppr-color-accent-pale-soft)",
          color: "var(--ppr-color-accent-navy)",
          fontFamily: "var(--ppr-typography-font-family-display)",
          fontSize: 14,
          marginTop: 2,
        }}
      >
        i
      </span>
      <div style={{ flex: "1 1 auto" }}>
        <div
          style={{
            fontFamily: "var(--ppr-typography-font-family-display)",
            fontSize: 11,
            letterSpacing: "0.10em",
            textTransform: "uppercase",
            color: "var(--ppr-color-accent-coral)",
            marginBottom: 4,
          }}
        >
          About the read
        </div>
        <p
          style={{
            fontFamily: "var(--ppr-typography-font-family-body)",
            fontSize: "var(--ppr-typography-font-size-sm)",
            lineHeight: "var(--ppr-typography-line-height-relaxed)",
            color: "var(--ppr-color-text-primary)",
            margin: 0,
          }}
        >
          Each person you add gets a structured behavioral read — how they
          decide, what moves them, and how to approach them. You'll see it
          shortened to "the read" or "Profile" throughout the product.{" "}
          <Link
            to="/science-behind-it"
            style={{
              color: "var(--ppr-color-accent-navy)",
              textDecoration: "underline",
              fontWeight: 600,
            }}
          >
            Read more on The Science Behind It →
          </Link>
        </p>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss help tooltip"
        data-testid="predisposition-profile-help-tooltip-dismiss"
        style={{
          flex: "0 0 auto",
          background: "transparent",
          border: "none",
          padding: 4,
          cursor: "pointer",
          fontFamily: "var(--ppr-typography-font-family-ui)",
          fontSize: 20,
          lineHeight: 1,
          color: "var(--ppr-color-text-muted)",
        }}
      >
        ×
      </button>
    </div>
  );
}
